import React, { useState, useCallback, useMemo } from 'react';
import {
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    CustomInput,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { parseDate } from 'utils';
import { useHistory } from 'react-router-dom';
import { useContractList, UPDATE_FOLDER, SET_MODAL, useContractListState } from 'context/contracts'
import useFolderActions from 'hooks/useFolderActions';
import Can from '../Utils/Can';
import clsx from 'clsx';
import { archiverFolder } from 'app-constants';
import TratoLoader from 'components/Shared/TratoLoader';

function MenuDropdown({ folder }) {

    const { t } = useTranslation();

    const [state, dispatch] = useContractList();
    const availableActions = useFolderActions(folder, null, {
        refetch: state.query,
        onUpdateFolder: (newFolder) => {
            dispatch({
                type: UPDATE_FOLDER, folder: {
                    ...folder,
                    ...newFolder
                }
            })
        },
        onOpenModal: ({ name }) => {
            dispatch({ type: SET_MODAL, name })
        }
    })

    return (
        <DropdownMenu className="contract-dropdown dropdown-menu">
            {
                availableActions.map((folderAction, i) => {
                    const { faIcon, label, onClick } = folderAction;
                    return (
                        <DropdownItem key={`${folderAction.name}-${i}`} onClick={onClick} className="contract-dropdown-item">
                            <i className={`fas fa-${faIcon} mr-2`}></i>{t(label)}
                        </DropdownItem>
                    )
                })
            }
        </DropdownMenu>
    )
}

function FolderItem({ folder, columns, subtitle, isMenuOpen, hasChilds, isChild, isChildOpen, onToggleChild = () => { }, hideMenu, onClick = () => { }, onMenuClick }) {

    const { t } = useTranslation();

    const handleClick = () => {
        onClick({ folder })
    }

    return (
        <tr onClick={handleClick} className={clsx('pointer', isChild && 'bg-light')}>
            <td className="align-middle text-nowrap text-truncate">
                <CustomInput type="checkbox" name="list-item" className="invisible" />
            </td>
            <td className="align-middle text-nowrap text-truncate">
                <div className="align-middle align-items-center" style={{ display: 'inline-flex' }}>
                    <img height="30px" alt="folder" className="document-type" src="/images/listing/folder.png" />
               </div>
            </td>
            <td className="text-left  text-nowrap text-truncate">
                <span className="d-flex" title={folder.name}>
                    {!isChild && hasChilds && (
                        <span className="text-info mr-1" name="child-button" role="button"><i className={`fas text-info fa-${isChildOpen ? 'minus' : 'plus'}`} onClick={onToggleChild}></i></span>
                    )}
                    {folder._id === archiverFolder ? t('contracts.listing.archiver') : folder.name}
                </span>
                <small>
                    {subtitle ? subtitle :
                        `${folder.recursiveCount} ${t('document', { count: folder.recursiveCount })}`
                    }
                </small>
            </td>
            {columns.includes('milestone') &&
                <Can privilege="VIEW_MILESTONES">
                    <td className="d-none d-lg-table-cell"></td>
                </Can>
            }
            {columns.includes('creator') &&
                <td></td>
            }
            {columns.includes('user') &&
                <td className="d-none d-lg-table-cell"></td>
            }
            {columns.includes('participants') &&
                <td></td>
            }
            {columns.includes('entered') &&
                <td></td>
            }
            {columns.includes('created') &&
                <td>
                    {folder.createdAt && 
                        <>
                            <span>{t('templates.listing.created')}</span>
                            <br></br>
                            <span>{parseDate(folder.createdAt, 'P')}</span>
                        </>
                    }
                </td>
            }
            {columns.includes('days') &&
                <td className="d-none d-lg-table-cell">
                </td>
            }
            {columns.includes('status') &&
                <td>
                </td>
            }
            {columns.includes('signature') &&
                <td className="d-none d-lg-table-cell">
                </td>
            }

            <td className="align-middle">
                {!hideMenu && <UncontrolledDropdown onClick={onMenuClick} hidden={folder._id === archiverFolder} direction="left">
                    <DropdownToggle color="light">
                        <i className="fas fa-bars"></i>
                    </DropdownToggle>
                    {isMenuOpen && <MenuDropdown folder={folder} />}
                </UncontrolledDropdown>
                }
            </td>
        </tr>
    )
}


function FolderItemContainer({ folder, columns, subtitle, toggleChildsOnClick }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isChildOpen, setIsChildOpen] = useState(false);
    const history = useHistory();
    const handleMenuClick = useCallback((e) => {
        e.stopPropagation();
        setIsMenuOpen(true);
    }, [])

    const handleClick = useCallback(({ folder }) => {
        history.push(`/contracts/list/${folder._id}`)
    }, [])

    const toggleChilds = useCallback((evt) => {
        if (evt.stopPropagation) evt.stopPropagation()
        setIsChildOpen(isOpen => !isOpen)
    }, [])

    const parentOnClick = useMemo(() => {
        if (toggleChildsOnClick) return toggleChilds
        return handleClick
    }, [toggleChildsOnClick, toggleChilds, handleClick])




    return (
        <>
            <FolderItem
                isMenuOpen={isMenuOpen}
                folder={folder}
                columns={columns}
                subtitle={subtitle}
                onMenuClick={handleMenuClick}
                onClick={parentOnClick}
                hasChilds={!!folder?.childs?.length}
                isChildOpen={isChildOpen}
                onToggleChild={toggleChilds}
                hideMenu
            />
            {isChildOpen &&
                folder.childs?.map(folderChild => (
                    <FolderItem
                        key={folderChild._id}
                        isMenuOpen={isMenuOpen}
                        folder={folderChild}
                        columns={columns}
                        onMenuClick={handleMenuClick}
                        onClick={handleClick}
                        isChild

                    />
                ))
            }
        </>
    )
}

function FolderListContainer({ columns }) {
    const { t } = useTranslation(['translation', 'glossary']);

    const { meta: { listOnly }, isLoadingFolders, folders } = useContractListState();

    // if (isLoadingFolders) return <TratoLoader className="text-center" />;

    if (!folders?.length || listOnly) return null;

    return (
        <FolderItemContainer
            folder={{
                name: t('myFolders'),
                childs: folders
            }}
            subtitle={`${folders?.length ?? 0} ${t('glossary:folder', { count: folders?.length ?? 0 })}`}
            columns={columns}
            toggleChildsOnClick
        />
    )
}


export default FolderListContainer;